/**
 * Permission expression governing the project → analytic-value mirror.
 *
 * ── WHY THIS LIVES IN @efacture/core ─────────────────────────────────
 * The mirror has TWO doors: the API route (`POST
 * /api/projects/:id/analytic-value`, plus the `createAnalyticValue` flag
 * on `POST /api/projects`) and the web screens that offer them. Shipping
 * the expression twice is exactly how the two ends drifted apart the
 * first time: the screens asked for `accounting:write` while the route
 * asked for `projects:manage`, so the button rendered for one set of
 * roles and answered 403 for it. One exported array, imported by both
 * ends, makes « the same expression » a fact rather than a claim
 * (CLAUDE.md Design Principle 1 — single source of truth).
 *
 * ── WHY THESE TWO PERMISSIONS ────────────────────────────────────────
 * `analytics:configure` is the GOVERNING half. Creating the mirror
 * creates (or adopts) an `AnalyticAxis` and creates an `AnalyticValue` —
 * the very objects `POST /api/accounting/analytics/axes` and
 * `POST /api/accounting/analytics/axes/:id/values` reserve to
 * `analytics:configure`. The funding referential is NOT a precedent for
 * skipping it: routes/funding/index.ts states that `funding:write`
 * implies axis creation *because those four axes are `isSystem: true`
 * and owned by that module*, and that « `analytics:configure` ne
 * gouverne donc que les axes DÉFINIS PAR L'UTILISATEUR ». Decision D-2
 * deliberately left the PROJECT axis `isSystem: false` — a user-defined
 * axis by the repository's own rule, hence squarely under
 * `analytics:configure`.
 *
 * `projects:read` is the SCOPING half: you may only mirror a project you
 * are allowed to see. It is deliberately NOT `projects:manage` — the act
 * never writes the `Project` row (D-3: `ProjectService.update` never
 * touches `code`, and the mirror action touches nothing else on the
 * project), so demanding the project-write right would demand a power
 * the act does not exercise. It also matches the router, where
 * `/projets/:id` — the screen carrying the button — is itself gated on
 * `projects:read`.
 *
 * ── WHAT IT GRANTS THAT WAS NOT ALREADY GRANTED: NOTHING ─────────────
 * A holder of `analytics:configure` can already hand-create that axis
 * and that value from « Axes analytiques ». The mirror only guarantees
 * that the value's code MATCHES the project's — which is the entire
 * accountant request behind the feature.
 *
 * ── WHICH SHIPPED ROLE CAN DO IT ─────────────────────────────────────
 * ACCOUNTANT (« Comptable ») — it already holds `analytics:configure`,
 * and `constants/roles.ts` grants it `projects:read` for this. That is
 * the UAT actor: the accountant who wanted to charge a class-6 expense
 * to a project. ADMIN matches through its `["*"]` wildcard. MANAGER
 * deliberately CANNOT: it owns projects but not the analytic
 * referential, and minting axes is the accountant's act.
 */
export const PROJECT_ANALYTIC_MIRROR_PERMISSIONS = [
    "projects:read",
    "analytics:configure",
];
//# sourceMappingURL=analytic-mirror-permissions.js.map